import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import Head from 'next/head';
import Link from 'next/link';
import type { ReactElement } from 'react';
import { Controller } from 'react-hook-form';
import type { InferType } from 'yup';
import { object, string } from 'yup';

import ArrowLeft from '@/assets/drawing-illustrations/ArrowLeft.svg';
import ArrowRight from '@/assets/drawing-illustrations/ArrowRight.svg';
import Checkmark from '@/assets/drawing-illustrations/Checkmark.svg';
import CoolEmoji from '@/assets/drawing-illustrations/cool-emoji.svg';
import Button from '@/components/Button';
import MultiLineInput from '@/components/Form/Fields/MultiLineInput';
import PhoneInputField from '@/components/Form/Fields/PhoneInputField';
import TextInputField from '@/components/Form/Fields/TextInputField';
import Layout from '@/components/Landing/Layout';
import Text from '@/components/Text';
import useForm from '@/hooks/useForm';
import { postRecruiterContactUs } from '@/services/common';
import { createMetaTags } from '@/utils/seo';

const ContactUs = () => {
  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
    control,
  } = useForm<InferType<typeof schema>>({
    resolver: yupResolver(schema),
  });
  const { mutate, isSuccess, reset } = useMutation({
    mutationFn: postRecruiterContactUs,
  });

  return (
    <>
      <style jsx global>
        {`
          body {
            background: #fafafa;
          }
        `}
      </style>
      <Head>
        {createMetaTags({
          title: 'Contact MindPal - AI Hiring & Recruitment Software',
          description:
            'AI-powered hiring and recruitment tools. Smart candidate sourcing, automated screening, and AI-driven job matching, resume builder, ATS. Get started today!',
        })}
      </Head>
      <div className="relative flex min-h-screen flex-1 items-center justify-center overflow-hidden px-5 py-36">
        <div className="absolute left-[-25%] top-0 aspect-square w-3/5 -translate-y-1/3 rounded-full bg-jb-primary-200 2xl:left-[-20%]">
          <div className="absolute bottom-1/4 left-1/3 w-1/2 rotate-6 ">
            <ArrowRight />
          </div>
        </div>
        <div className="absolute bottom-0 right-[-25%] aspect-square w-3/5 translate-y-1/3 rounded-full bg-jb-secondary-200 2xl:right-[-20%] ">
          <div className="absolute left-1/4 top-1/3 w-1/2 rotate-6 ">
            <ArrowLeft />
          </div>
        </div>
        <div className="z-10 flex w-full max-w-[35rem] flex-col gap-6 rounded-[1.25rem] bg-jb-neutral-100 p-8 text-black shadow-2xl xl:max-w-[40rem] ">
          {isSuccess ? (
            <div className="flex flex-col items-center gap-8">
              <Checkmark />
              <div className="flex flex-col gap-4">
                <Text variant="jb-h2" className="text-center font-semibold">
                  File successfully uploaded!
                </Text>
                <Text className="flex items-center gap-2 self-center text-center">
                  We will get back to you shortly <CoolEmoji />
                </Text>
              </div>
              <div className="flex w-full gap-4">
                <Button
                  className="w-full"
                  variant="candidate-secondary"
                  onClick={() => {
                    reset();
                    resetForm();
                  }}
                >
                  Back
                </Button>
                <Link tabIndex={-1} href="/" className="w-full">
                  <Button className="w-full" variant="jbPrimary">
                    Go to Homepage
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-4">
                <Text variant="jb-h3" className="text-center font-semibold">
                  Contact Us - MindPal for Recruiters
                </Text>
                <Text className="whitespace-pre-wrap text-center ">
                  Have questions or want a demo? Fill out the form, and our team
                  will get back to you ASAP!
                </Text>
              </div>
              <form
                onSubmit={handleSubmit((data) => mutate(data))}
                className="flex flex-col gap-1 [&>div>div]:gap-2 [&_*]:after:!text-jb-body-large [&_label]:text-jb-body-medium"
              >
                <TextInputField
                  required
                  label="First Name"
                  variant="jobboardGrayBackgroud"
                  {...register('firstName')}
                  error={errors.firstName?.message}
                />
                <TextInputField
                  required
                  label="Last Name"
                  variant="jobboardGrayBackgroud"
                  {...register('lastName')}
                  error={errors.lastName?.message}
                />
                <TextInputField
                  required
                  label="Company"
                  variant="jobboardGrayBackgroud"
                  {...register('company')}
                  error={errors.company?.message}
                />
                <TextInputField
                  required
                  label="E-mail"
                  variant="jobboardGrayBackgroud"
                  {...register('email')}
                  error={errors.email?.message}
                />
                <div>
                  <Controller
                    control={control}
                    name="phone"
                    render={({ field: { onChange, value, name, onBlur } }) => (
                      <PhoneInputField
                        required
                        label="Phone number"
                        variant="jobboardGrayBackground"
                        onChange={onChange}
                        value={value}
                        name={name}
                        onBlur={onBlur}
                        error={errors.phone}
                      />
                    )}
                  />
                </div>
                <MultiLineInput
                  label="Message"
                  variant="jobboardGrayBackgroud"
                  {...register('message')}
                  error={errors.message?.message}
                  rows={3}
                />
                <Button
                  type="submit"
                  variant="jbPrimary"
                  className="w-full text-white"
                >
                  Send
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ContactUs;

ContactUs.getLayout = (page: ReactElement) => (
  <Layout theme="light">{page}</Layout>
);

const schema = object({
  firstName: string().required('This field is required!'),
  lastName: string().required('This field is required!'),
  company: string().required('This field is required!'),
  email: string()
    .email('Provide a valid email')
    .required('This field is required!'),
  phone: string()
    .min(7, 'Provide a valid phone number')
    .max(15, 'Provide a valid phone number')
    .defined('This field is required'),
  message: string(),
});
